// src/components/editor/modals/EbookPreviewModal.jsx
//
// Read-only "Ebook Preview" — manuscript as it would look on an e-reader.
// Each editor page is shown as a full-height screen block (proportional to the
// device frame) so the layout is always consistent, even for short pages.
// Content reflows inside each block; empty filler paragraphs are stripped.
//
// Paged devices (tablets, e-readers): pages are a swipeable carousel (swipe left =
// next, right = prev) to mimic e-reader page turns. Phones keep a continuous
// vertical scroll stack instead, matching how most phone reading apps behave.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconButton, ListSubheader, MenuItem, Select, Slider, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ScreenRotationIcon from '@mui/icons-material/ScreenRotation';

// Returns one HTML string per editor page, stripping print-layout padding.
function extractPages(html) {
  try {
    const doc = new DOMParser().parseFromString(html || '', 'text/html');
    const bodies = doc.querySelectorAll('.rv-page-body');
    if (!bodies.length) return [html || ''];
    const pages = [];
    bodies.forEach((body) => {
      const clone = body.cloneNode(true);
      clone
        .querySelectorAll('.rv-end-spacer, .rv-page-number, .rv-page-header, .rv-page-footer')
        .forEach((n) => n.remove());
      // Remove empty block elements that were only padding the print page height.
      clone.querySelectorAll('p, h1, h2, h3, h4, li').forEach((el) => {
        if (!el.textContent.trim() && !el.querySelector('img')) el.remove();
      });
      // Strip inline font-size and font-family so the reader's font controls
      // apply uniformly across all pages (inline styles override CSS inheritance).
      clone.querySelectorAll('[style]').forEach((el) => {
        el.style.removeProperty('font-size');
        el.style.removeProperty('font-family');
        if (!el.getAttribute('style').trim()) el.removeAttribute('style');
      });
      const content = clone.innerHTML.trim();
      if (content) pages.push(content);
    });
    return pages.length ? pages : [html || ''];
  } catch {
    return [html || ''];
  }
}

// Sizes are each device's CSS/logical points (not raw hardware pixels), the same
// unit real browsers report — so these proportions match what the device actually
// shows. Tablets and e-readers page like a book; phones keep the scrolling feed.
const DEVICE_GROUPS = [
  {
    group: 'Tablets',
    paged: true,
    devices: [
      { key: 'ipad-mini', label: 'iPad Mini', width: 744, height: 1133, radius: 24 },
      { key: 'ipad-air', label: 'iPad Air', width: 820, height: 1180, radius: 24 },
      { key: 'ipad-pro', label: 'iPad Pro 12.9"', width: 1024, height: 1366, radius: 24 },
    ],
  },
  {
    group: 'Phones',
    paged: false,
    devices: [
      { key: 'iphone-se', label: 'iPhone SE', width: 375, height: 667, radius: 32 },
      { key: 'iphone-15', label: 'iPhone 15', width: 393, height: 852, radius: 32 },
      { key: 'pixel-8', label: 'Pixel 8', width: 412, height: 915, radius: 32 },
    ],
  },
  {
    group: 'E-readers',
    paged: true,
    devices: [
      { key: 'kindle-paperwhite', label: 'Kindle Paperwhite', width: 480, height: 648, radius: 14 },
      { key: 'kobo-libra', label: 'Kobo Libra', width: 528, height: 704, radius: 14 },
    ],
  },
];

const DEVICES_BY_KEY = Object.fromEntries(
  DEVICE_GROUPS.flatMap((g) => g.devices.map((d) => [d.key, { ...d, group: g.group, paged: g.paged }]))
);
const DEFAULT_DEVICE_KEY = 'ipad-air';

const deviceLabel = (d) => `${d.label} (${d.width} × ${d.height})`;

// Device frame: padding 14 each side → screen height = height - 28.
// Screen inner: padding 28px top/bottom → content area = height - 28 - 56.
const pageContentHeight = (dev) => dev.height - 28 - 56;

// Swipe tuning: fraction of slide width (or a fast flick) needed to commit a page turn.
const SWIPE_COMMIT_RATIO = 0.25;
const SWIPE_VELOCITY_PX_PER_MS = 0.5;
const AXIS_LOCK_THRESHOLD = 8;

export default function EbookPreviewModal({ open, onClose, html = '', title = 'Ebook Preview' }) {
  const [deviceKey, setDeviceKey] = useState(DEFAULT_DEVICE_KEY);
  const [orientation, setOrientation] = useState('portrait');
  const DEFAULT_FONT_SIZE = 18;
  const [fontSize, setFontSize] = useState(DEFAULT_FONT_SIZE);

  const selectedDevice = DEVICES_BY_KEY[deviceKey] || DEVICES_BY_KEY[DEFAULT_DEVICE_KEY];
  const isLandscape = selectedDevice.paged && orientation === 'landscape';
  const dev = isLandscape
    ? { ...selectedDevice, width: selectedDevice.height, height: selectedDevice.width }
    : selectedDevice;

  const pages = useMemo(() => (open ? extractPages(html) : []), [open, html]);

  // --- Paged-device carousel state ---
  const [currentPage, setCurrentPage] = useState(0);
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [containerWidth, setContainerWidth] = useState(selectedDevice.width);
  const viewportRef = useRef(null);
  const dragRef = useRef({ startX: 0, startY: 0, startT: 0, axis: null, active: false });

  const goToPage = useCallback((idx) => {
    setCurrentPage((p) => Math.max(0, Math.min(pages.length - 1, idx)));
  }, [pages.length]);

  // Reset to page 1 whenever the preview is (re)opened.
  useEffect(() => {
    if (open) setCurrentPage(0);
  }, [open]);

  // Keep the index in range if the manuscript content changes underneath us.
  useEffect(() => {
    setCurrentPage((p) => Math.max(0, Math.min(pages.length - 1, p)));
  }, [pages]);

  // Measure the device screen so the carousel can shift by exact pixel widths.
  useEffect(() => {
    if (!selectedDevice.paged) return;
    const el = viewportRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setContainerWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [selectedDevice.paged]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { onClose?.(); return; }
      if (!selectedDevice.paged || pages.length < 2) return;
      if (e.target?.closest?.('.MuiSlider-root')) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); goToPage(currentPage - 1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goToPage(currentPage + 1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, selectedDevice.paged, pages.length, currentPage, goToPage]);

  if (!open) return null;

  const pageH = pageContentHeight(dev);

  const onPointerDown = (e) => {
    dragRef.current = { startX: e.clientX, startY: e.clientY, startT: performance.now(), axis: null, active: true };
    setIsDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    const ds = dragRef.current;
    if (!ds.active) return;
    const dx = e.clientX - ds.startX;
    const dy = e.clientY - ds.startY;
    if (!ds.axis) {
      if (Math.abs(dx) < AXIS_LOCK_THRESHOLD && Math.abs(dy) < AXIS_LOCK_THRESHOLD) return;
      ds.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
    if (ds.axis === 'y') return; // vertical read-scroll inside the page handles this natively
    let next = dx;
    // Rubber-band past the first/last page instead of allowing a full free drag.
    if ((currentPage === 0 && dx > 0) || (currentPage === pages.length - 1 && dx < 0)) {
      next = dx / 3;
    }
    setDragX(next);
  };

  const endDrag = () => {
    const ds = dragRef.current;
    if (ds.active && ds.axis === 'x') {
      const dt = Math.max(1, performance.now() - ds.startT);
      const velocity = Math.abs(dragX) / dt;
      const committed = Math.abs(dragX) > containerWidth * SWIPE_COMMIT_RATIO || velocity > SWIPE_VELOCITY_PX_PER_MS;
      if (committed) {
        goToPage(dragX < 0 ? currentPage + 1 : currentPage - 1);
      }
    }
    dragRef.current.active = false;
    setIsDragging(false);
    setDragX(0);
  };

  return (
    <div
      role="dialog"
      aria-label="Ebook preview"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(20, 18, 30, 0.78)', display: 'flex', flexDirection: 'column' }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '10px 16px', background: '#2A2640', color: '#fff', flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
        <Typography sx={{ fontWeight: 600, flex: 1 }}>{title}</Typography>

        <Select
          size="small" value={deviceKey}
          onChange={(e) => setDeviceKey(e.target.value)}
          MenuProps={{
            style: { zIndex: 2100 },
            slotProps: {
              paper: {
                sx: {
                  mt: 1,
                  background: '#2A2640',
                  border: '1px solid rgba(212,175,55,0.3)',
                  borderRadius: '10px',
                  boxShadow: '0 12px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(212,175,55,0.08)',
                  '& .MuiList-root': { py: 0.5 },
                },
              },
            },
          }}
          sx={{
            color: '#fff', minWidth: 220,
            background: 'rgba(65,59,141,0.4)',
            borderRadius: '8px',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(212,175,55,0.35)' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(212,175,55,0.6)' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37', borderWidth: '1.5px' },
            '& .MuiSvgIcon-root': { color: '#F5D76E' },
          }}
        >
          {DEVICE_GROUPS.flatMap((g) => [
            <ListSubheader
              key={g.group}
              sx={{
                lineHeight: '32px', background: '#241F3A', color: '#F5D76E',
                fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                borderBottom: '1px solid rgba(212,175,55,0.15)',
              }}
            >
              {g.group}
            </ListSubheader>,
            ...g.devices.map((d) => (
              <MenuItem
                key={d.key} value={d.key}
                sx={{
                  color: '#eee', fontSize: 14,
                  '&:hover': { background: 'rgba(212,175,55,0.12)' },
                  '&.Mui-selected': { background: 'rgba(212,175,55,0.18)', color: '#F5D76E' },
                  '&.Mui-selected:hover': { background: 'rgba(212,175,55,0.24)' },
                }}
              >
                {deviceLabel(d)}
              </MenuItem>
            )),
          ])}
        </Select>

        {selectedDevice.paged && (
          <IconButton
            aria-label="Rotate device"
            title="Rotate device"
            onClick={() => setOrientation((o) => (o === 'portrait' ? 'landscape' : 'portrait'))}
            size="small"
            sx={{ color: '#ddd', transform: orientation === 'landscape' ? 'rotate(90deg)' : 'none', transition: 'transform 200ms' }}
          >
            <ScreenRotationIcon fontSize="small" />
          </IconButton>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: 180 }}>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>A</Typography>
          <Slider
            size="small" min={12} max={28} value={fontSize}
            onChange={(_, v) => setFontSize(v)}
            marks={[{ value: DEFAULT_FONT_SIZE }]}
            sx={{
              color: '#F5D76E',
              '& .MuiSlider-mark': { background: '#fff', width: 3, height: 3, borderRadius: '50%', opacity: 0.7 },
            }}
          />
          <Typography variant="caption" sx={{ opacity: 0.8, fontSize: 18 }}>A</Typography>
        </div>

        <IconButton aria-label="Close preview" onClick={onClose} size="small" sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </div>

      {/* Stage */}
      <div style={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', padding: '32px 16px' }}>
        <div style={{ width: dev.width, maxWidth: '100%', height: dev.height, background: '#1c1c1e', borderRadius: dev.radius, padding: 14, boxShadow: '0 18px 50px rgba(0,0,0,0.45)', flexShrink: 0 }}>
          {selectedDevice.paged ? (
            // Screen — swipeable carousel, one manuscript page per slide.
            <div
              ref={viewportRef}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              style={{
                width: '100%', height: '100%', background: '#faf7f0', borderRadius: dev.radius - 8,
                overflow: 'hidden', position: 'relative', touchAction: 'pan-y',
              }}
            >
              {pages.length === 0 && (
                <div style={{ padding: '28px 24px', color: '#aaa', fontStyle: 'italic', fontSize: 15 }}>Nothing to preview yet.</div>
              )}
              {pages.length > 0 && (
                <div
                  style={{
                    display: 'flex', height: '100%',
                    width: containerWidth * pages.length,
                    transform: `translateX(${-currentPage * containerWidth + dragX}px)`,
                    transition: isDragging ? 'none' : 'transform 280ms cubic-bezier(.22,.61,.36,1)',
                  }}
                >
                  {pages.map((pageHtml, i) => (
                    <div
                      key={i}
                      style={{
                        flexShrink: 0, width: containerWidth, height: '100%', overflowY: 'auto', overflowX: 'hidden',
                        boxSizing: 'border-box', padding: '28px 24px', fontSize,
                        fontFamily: 'Georgia, "Times New Roman", serif', lineHeight: 1.65, color: '#1a1a1a',
                      }}
                    >
                      <div style={{ minHeight: pageH, textAlign: 'justify', hyphens: 'auto' }} dangerouslySetInnerHTML={{ __html: pageHtml }} />
                    </div>
                  ))}
                </div>
              )}
              {pages.length > 1 && (
                <div style={{
                  position: 'absolute', bottom: 10, left: 0, right: 0, textAlign: 'center',
                  fontSize: 12, color: '#9a9284', pointerEvents: 'none', userSelect: 'none',
                }}>
                  {currentPage + 1} / {pages.length}
                </div>
              )}
            </div>
          ) : (
            // Screen — font-size set here so ALL pages inherit it uniformly
            <div style={{ width: '100%', height: '100%', background: '#faf7f0', borderRadius: dev.radius - 8, overflow: 'auto', padding: '28px 24px', boxSizing: 'border-box', fontSize, fontFamily: 'Georgia, "Times New Roman", serif', lineHeight: 1.65, color: '#1a1a1a' }}>
              {pages.length === 0 && (
                <div style={{ color: '#aaa', fontStyle: 'italic', fontSize: 15 }}>Nothing to preview yet.</div>
              )}
              {pages.map((pageHtml, i) => (
                <React.Fragment key={i}>
                  {/* Each page occupies at least one full screen height so proportions
                      are preserved even when a page has only a few words. */}
                  <div
                    style={{ minHeight: pageH, textAlign: 'justify', hyphens: 'auto' }}
                    dangerouslySetInnerHTML={{ __html: pageHtml }}
                  />
                  {/* Separator between pages — thin line flush with page bottom */}
                  {i < pages.length - 1 && (
                    <div style={{ height: 1, background: '#d0c8b8', margin: '0 0 28px' }} />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
